import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

type SeoDataType = {
  [key: string]: {
    title: string;
    description: string;
    keywords: string;
  };
};

export const SEO: React.FC = () => {
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const currentLang = i18n.language;
  const isRTL = currentLang === 'ar';

  // 根据路径判断是否是博客或法律页面
  const isBlogPage = location.pathname.startsWith('/blog');
  const isPrivacyPage = location.pathname === '/privacy-policy';
  const isTermsPage = location.pathname === '/terms';

  const seoData: SeoDataType = {
    en: {
      title: 'Secure Anonymous TempMail - Protect your privacy And Avoid Spam',
      description: 'Create instant disposable email addresses. Protect your privacy, avoid spam, and manage online registrations securely. Free, anonymous, and no registration required.',
      keywords: 'temporary email, disposable email, temp mail, anonymous email, fake email, temporary inbox, email privacy, spam protection',
    },
    ar: {
      title: 'بريد مؤقت آمن ومجهول - حماية خصوصيتك وتجنب الرسائل المزعجة',
      description: 'إنشاء عناوين بريد إلكتروني مؤقتة فورية. حماية خصوصيتك، تجنب الرسائل غير المرغوب فيها، وإدارة التسجيلات عبر الإنترنت بشكل آمن. مجاني، مجهول، ولا يتطلب التسجيل.',
      keywords: 'بريد إلكتروني مؤقت، بريد مؤقت، بريد وهمي، خصوصية البريد الإلكتروني، حماية من الرسائل المزعجة',
    },
    zh: {
      title: '安全匿名临时邮箱 - 保护您的隐私并避免垃圾邮件',
      description: '创建即时一次性邮箱地址。保护您的隐私，避免垃圾邮件，安全管理在线注册。免费，匿名，无需注册。',
      keywords: '临时邮箱, 一次性邮箱, 匿名邮箱, 虚拟邮箱, 邮箱隐私, 垃圾邮件保护',
    },
    ja: {
      title: '安全匿名メール - プライバシーを保護しスパムを回避',
      description: '即時使い捨てメールアドレスを作成。プライバシーを保護し、スパムを避け、オンライン登録を安全に管理。無料、匿名、登録不要。',
      keywords: '仮想メール, 使い捨てメール, 匿名メール, フェイクメール, メールプライバシー, スパム対策',
    },
    ko: {
      title: '안전한 익명 임시메일 - 개인정보 보호 및 스팸 방지',
      description: '즉시 일회용 이메일 주소 생성. 개인정보 보호, 스팸 방지, 안전한 온라인 등록 관리. 무료, 익명, 가입 불필요.',
      keywords: '임시 이메일, 일회용 이메일, 익명 이메일, 가짜 이메일, 이메일 프라이버시, 스팸 방지',
    },
    es: {
      title: 'TempMail Anónimo Seguro - Servicio de Correo Temporal',
      description: 'Crea direcciones de correo desechables al instante. Protege tu privacidad, evita el spam y gestiona registros en línea de forma segura. Gratis, anónimo y sin registro.',
      keywords: 'correo temporal, correo desechable, correo temporal, correo anónimo, correo falso, bandeja temporal, privacidad de correo, protección contra spam',
    },
    fr: {
      title: 'TempMail Anonyme Sécurisé - Protégez votre vie privée et évitez le spam',
      description: 'Créez des adresses email jetables instantanément. Protégez votre vie privée, évitez le spam et gérez vos inscriptions en ligne en toute sécurité. Gratuit, anonyme et sans inscription.',
      keywords: 'email temporaire, email jetable, messagerie temporaire, email anonyme, faux email, boîte de réception temporaire, confidentialité email, protection spam',
    },
    de: {
      title: 'Sicherer Anonymer TempMail - Schützen Sie Ihre Privatsphäre und vermeiden Sie Spam',
      description: 'Erstellen Sie sofort Wegwerf-E-Mail-Adressen. Schützen Sie Ihre Privatsphäre, vermeiden Sie Spam und verwalten Sie Online-Registrierungen sicher. Kostenlos, anonym und ohne Registrierung.',
      keywords: 'temporäre Email, Wegwerf-Email, temp Mail, anonyme Email, gefälschte Email, temporärer Posteingang, Email-Privatsphäre, Spam-Schutz',
    },
    pt: {
      title: 'TempMail Anônimo Seguro - Proteja sua privacidade e evite spam',
      description: 'Crie endereços de email descartáveis instantaneamente. Proteja sua privacidade, evite spam e gerencie registros online com segurança. Gratuito, anônimo e sem necessidade de registro.',
      keywords: 'email temporário, email descartável, temp mail, email anônimo, email falso, caixa de entrada temporária, privacidade de email, proteção contra spam',
    },
    ru: {
      title: 'Безопасная Анонимная TempMail - Защитите конфиденциальность и избегайте спама',
      description: 'Создавайте мгновенные одноразовые email адреса. Защитите свою конфиденциальность, избегайте спама и безопасно управляйте онлайн-регистрациями. Бесплатно, анонимно и без регистрации.',
      keywords: 'временная почта, одноразовый email, временный ящик, анонимная почта, фейковый email, временный почтовый ящик, конфиденциальность email, защита от спама',
    },
    it: {
      title: 'TempMail Anonima Sicura - Servizio Email Temporaneo',
      description: 'Crea istantaneamente indirizzi email usa e getta. Proteggi la tua privacy, evita lo spam e gestisci le registrazioni online in modo sicuro. Gratuito, anonimo e senza registrazione.',
      keywords: 'email temporanea, email usa e getta, temp mail, email anonima, email falsa, casella temporanea, privacy email, protezione spam',
    }
  };

  // 获取当前页面的 SEO 数据
  const currentSeoData = seoData[currentLang] || seoData.en;

  // 生成 canonical URL
  const getCanonicalUrl = () => {
    // 博客和法律页面使用原始路径作为 canonical URL
    if (isBlogPage || isPrivacyPage || isTermsPage) {
      // 移除末尾的斜杠（如果存在）
      const path = location.pathname.replace(/\/$/, '');
      return `https://tempmail.fastbytelab.com${path}`;
    }

    // 英文版本使用根路径，其他语言版本使用带语言前缀的路径
    return currentLang === 'en' 
      ? 'https://tempmail.fastbytelab.com/' 
      : `https://tempmail.fastbytelab.com/${currentLang}/`;
  };

  // 直接修改 index.html 的内容
  useEffect(() => {
    // 更新 title
    document.title = currentSeoData.title;

    // 更新 meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', currentSeoData.description);
    }

    // 更新 meta title
    const metaTitle = document.querySelector('meta[name="title"]');
    if (metaTitle) {
      metaTitle.setAttribute('content', currentSeoData.title);
    }

    // 更新 Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const ogDescription = document.querySelector('meta[property="og:description"]');
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogTitle) ogTitle.setAttribute('content', currentSeoData.title);
    if (ogDescription) ogDescription.setAttribute('content', currentSeoData.description);
    if (ogUrl) ogUrl.setAttribute('content', getCanonicalUrl());

    // 更新 Twitter Card tags
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    const twitterUrl = document.querySelector('meta[name="twitter:url"]');
    if (twitterTitle) twitterTitle.setAttribute('content', currentSeoData.title);
    if (twitterDescription) twitterDescription.setAttribute('content', currentSeoData.description);
    if (twitterUrl) twitterUrl.setAttribute('content', getCanonicalUrl());

    // 更新 canonical URL
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      const canonicalUrl = getCanonicalUrl();
      canonicalLink.setAttribute('href', canonicalUrl);
    }

    // 更新 HTML 标签的语言和方向
    document.documentElement.lang = currentLang;
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';

    // 更新隐藏的 H1 标题
    const hiddenH1 = document.querySelector('h1[style*="position: absolute"]');
    if (hiddenH1) {
      hiddenH1.textContent = currentSeoData.title;
    }
  }, [currentLang, location.pathname]);

  return (
    <Helmet>
      <html lang={currentLang} dir={isRTL ? 'rtl' : 'ltr'} />
      <title>{currentSeoData.title}</title>
      <meta name="title" content={currentSeoData.title} />
      <meta name="description" content={currentSeoData.description} />
      <meta name="keywords" content={currentSeoData.keywords} />
      
      {/* Open Graph tags */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={getCanonicalUrl()} />
      <meta property="og:title" content={currentSeoData.title} />
      <meta property="og:description" content={currentSeoData.description} />
      <meta property="og:image" content="https://tempmail.fastbytelab.com/og-image.jpg" />
      
      {/* Twitter Card tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={getCanonicalUrl()} />
      <meta name="twitter:title" content={currentSeoData.title}/>
      <meta name="twitter:description" content={currentSeoData.description} />
      <meta name="twitter:image" content="https://tempmail.fastbytelab.com/og-image.jpg" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={getCanonicalUrl()} />
      
      {/* Schema.org markup */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebApplication',
          'name': t('app.title'),
          'url': getCanonicalUrl(),
          'applicationCategory': 'UtilitiesApplication',
          'description': currentSeoData.description,
          'offers': {
            '@type': 'Offer',
            'price': '0',
            'priceCurrency': 'USD'
          }
        })}
      </script>
    </Helmet>
  );
};